import { useEffect, useState } from 'react';
import { useSlateSelector } from 'slate-react';
import { Editor, Range } from 'slate';
import { useEditorContext } from 'app/state/contexts/EditorContext';

const selectHasExpandedSelection = (editor: Editor): boolean => !!editor.selection && !Range.isCollapsed(editor.selection);

type SelectionRoot = { getSelection: () => Selection | null };

const isSelectionRoot = (node: object): node is SelectionRoot => typeof (node as { getSelection?: unknown }).getSelection === 'function';

export const useShadowSelectionMarked = (): boolean => {
  const ctx = useEditorContext();
  const shadowHost = ctx?.shadowHost;
  const hasExpandedSelection = useSlateSelector(selectHasExpandedSelection);
  const [marked, setMarked] = useState(false);

  useEffect(() => {
    const shadowRoot = shadowHost?.shadowRoot;
    if (!shadowRoot) return undefined;

    const update = (event?: Event) => {
      if (event?.type === 'blur') {
        setMarked(false);
        return;
      }
      const root: SelectionRoot = isSelectionRoot(shadowRoot) ? shadowRoot : shadowRoot.ownerDocument;
      const selection = root.getSelection();
      if (!selection) {
        setMarked(false);
        return;
      }
      const isCollapsed = selection.anchorNode === selection.focusNode && selection.anchorOffset === selection.focusOffset;
      setMarked(!isCollapsed);
    };

    const events = ['mouseup', 'keyup'] as const;
    const options = { capture: true } as const;
    events.forEach((e) => shadowRoot.addEventListener(e, update, options));
    events.forEach((e) => window.addEventListener(e, update, options));
    update();
    return () => {
      events.forEach((e) => shadowRoot.removeEventListener(e, update, options));
      events.forEach((e) => window.removeEventListener(e, update, options));
    };
  }, [shadowHost]);

  if (shadowHost?.shadowRoot) return marked;
  return hasExpandedSelection;
};
